import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageBreadcrumb from '@/components/common/PageBreadCrumb';
import PageMeta from '@/components/common/PageMeta';
import { useClient } from '@/features/clients/hooks/useClient';
import Badge from '@/components/ui/badge/Badge';
import Button from '@/components/ui/button/Button';
import { useRentings, RENTINGS_PER_PAGE } from '@/features/rentings/hooks/useRentings';
import { RentingsTable } from '@/features/rentings/components/RentingsTable';
import { useCars } from '@/features/cars/hooks/useCars';
import { Loader } from '@/components/ui/loader/Loader';
import Pagination from '@/components/common/Pagination';
import { TableColumnsFilter } from '@/components/ui/table/TableColumnsFilter';
import { ColumnFilter } from '@/components/ui/table/ColumnFilter';
import { ClientVerificationModal } from '@/features/clients/components/ClientVerificationModal';
import { ArchiveVerificationStatus } from '@/features/clients/components/ArchiveVerificationStatus';
import { useModal } from '@/shared/hooks/useModal';
import DetailsCard from '@/components/common/DetailsCard';

// Определяем столбцы для таблицы аренд клиента
const CLIENT_RENTINGS_COLUMNS = [
  { key: 'id', header: 'Renting Id', field: 'id' },
  { key: 'car', header: 'Car', field: 'car' },
  { key: 'delivery', header: 'Delivery', field: 'delivery' },
  { key: 'status', header: 'Status', field: 'status' },
  { key: 'price', header: 'Total Price', field: 'total_price' },
  { key: 'reports', header: 'Reports', field: 'reports' },
  { key: 'actions', header: 'Actions', field: 'actions' },
];

// Ключ для хранения настроек видимости столбцов
const CLIENT_RENTINGS_TABLE_STORAGE_KEY = 'client-rentings-table-columns';

// Столбцы, видимые по умолчанию
const DEFAULT_VISIBLE_COLUMNS = ['car', 'delivery', 'status', 'price', 'reports', 'actions'];

const ClientDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const { data: client, isLoading, error } = useClient(id!);
  const { data: rentingsData, isLoading: rentingsLoading } = useRentings(currentPage, { client: id });
  const { data: cars, isLoading: carsLoading } = useCars({ limit: 300 });
  const { isOpen: isVerificationOpen, openModal: openVerificationModal, closeModal: closeVerificationModal } = useModal();

  // Handle scroll to verification block and highlight when hash is present
  useEffect(() => {
    if (location.hash === '#verification-status' && !isLoading && client) {
      // Wait for the page to render
      setTimeout(() => {
        const element = document.getElementById('verification-status');
        if (element) {
          // Get header height (sticky header)
          const header = document.querySelector('header.sticky');
          const headerHeight = header ? header.getBoundingClientRect().height : 0;

          // Calculate position with header height + 20px offset from top
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - headerHeight - 20;

          // Scroll to element with offset
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });

          // Add highlight class
          element.classList.add('highlight-border');

          // Remove highlight after 1 second
          setTimeout(() => {
            element.classList.remove('highlight-border');
          }, 1000);
        }
      }, 100);
    }
  }, [location.hash, isLoading, client]);

  if (isLoading) {
    return <Loader className='h-full w-full mt-60' />;
  }

  if (error || !client) {
    return <div>{t('clients.details.error_loading')}</div>;
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getCountryName = (countryCode: string) => {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(countryCode);
  }

  const getCountryFlag = (countryCode: string) => {
    return countryCode
        .toUpperCase()
        .split('')
        .map(char => String.fromCodePoint(0x1F1E6 + char.charCodeAt(0) - 65))
        .join('');
  }

  const getVerificationBadgeProps = (status: string) => {
    switch (status) {
      case 'verified':
        return { color: 'success' as const, variant: 'light' as const };
      case 'verification_needed':
        return { color: 'warning' as const, variant: 'light' as const };
      case 'new':
        return { color: 'info' as const, variant: 'light' as const };
      default:
        return { color: 'light' as const, variant: 'light' as const };
    }
  };

  return (
    <>
      <PageMeta
        title={`${client.first_name} ${client.last_name} | Admin.Drivitive`}
        description={t('clients.details.title')}
      />
      <PageBreadcrumb
        items={[
          { label: t('nav.clients'), path: '/clients' },
          { label: `${client.first_name} ${client.last_name}` }
        ]}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 xl:grid-cols-3 2xl:gap-7.5">
        {/* Personal Information */}
        <DetailsCard
          title={t('clients.details.personal_info')}
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm text-text-tertiary">{t('clients.details.full_name')}</p>
              <p className="font-medium text-text-primary">
                {client.first_name} {client.last_name}
              </p>
            </div>
            <div>
              <p className="text-sm text-text-tertiary">{t('clients.details.client_type')}</p>
              <Badge size="sm" color="primary">
                {client.client_type.toUpperCase()}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-text-tertiary">{t('clients.details.language')}</p>
              <p className="font-medium text-text-primary">
                {client.language.toUpperCase()}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-text-tertiary">{t('clients.details.country')}</p>
                <div className="flex items-center gap-2">
                  <p className="font-large text-text-primary">{getCountryFlag(client.country)}</p>
                  <p className="font-medium text-text-primary">{getCountryName(client.country)}</p>
                </div>
              </div>
              {client.city && (
                <div>
                  <p className="text-sm text-text-tertiary">{t('clients.details.city')}</p>
                  <p className="font-medium text-text-primary">{client.city}</p>
                </div>
              )}
              {client.address && (
                <div>
                  <p className="text-sm text-text-tertiary">{t('clients.details.address')}</p>
                  <p className="font-medium text-text-primary">{client.address}</p>
                </div>
              )}
              {client.post_index && (
                <div>
                  <p className="text-sm text-text-tertiary">{t('clients.details.postal_code')}</p>
                  <p className="font-medium text-text-primary">{client.post_index}</p>
                </div>
              )}
            </div>
            <div>
              <p className="text-sm text-text-tertiary">{t('clients.details.registered')}</p>
              <p className="font-medium text-text-primary">
                {new Date(client.registered_at).toLocaleString()}
              </p>
            </div>
          </div>
        </DetailsCard>

        {/* Contact Information */}
        <DetailsCard
          title={t('clients.details.contact_info')}
        >
          <div className="space-y-4">
            <div>
              <p className="flex gap-1 mb-1 items-center text-sm text-text-tertiary">
                <span>{t('clients.details.email')}</span>
                <Badge
                  size="sm"
                  color={client.is_email_confirmed ? 'success' : 'error'}
                >
                  {client.is_email_confirmed ? t('clients.details.verified') : t('clients.details.not_verified')}
                </Badge>
              </p>
              <div className="flex items-center gap-2">
                <a
                  href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(client.email)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:underline"
                >
                  {client.email}
                </a>
              </div>
            </div>
            <div>
              <p className="flex gap-1 mb-1 items-center text-sm text-text-tertiary">
                <span>{t('clients.details.phone')}</span>
                <Badge
                  size="sm"
                  color={client.is_phone_confirmed ? 'success' : 'error'}
                >
                  {client.is_phone_confirmed ? t('clients.details.verified') : t('clients.details.not_verified')}
                </Badge>
              </p>
              <div className="flex items-center gap-2">
                <p className="font-medium text-text-primary">{client.phone}</p>
              </div>
            </div>
          </div>
        </DetailsCard>


        {/* Verification Status */}
        <DetailsCard
          id="verification-status"
          title={t('clients.details.verification_status')}
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm text-text-tertiary">{t('Status')}</p>
              <Badge {...getVerificationBadgeProps(client.verification_status)} size="sm">
                {t(`clients.filters.status.${client.verification_status}`)}
              </Badge>
            </div>

            {/* Archive Verification Status */}
            <ArchiveVerificationStatus
              clientId={id!}
              verificationStatus={client.verification_status}
            />
          </div>
        </DetailsCard>
      </div>

      {/* Client Rentings */}
      <div className="rounded-2xl mt-8 border border-border-primary bg-bg-card p-4 md:p-6 xl:p-7.5">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-4">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-text-primary">{t('clients.details.client_rentings')}</h2>
          </div>
          {!rentingsLoading && !carsLoading && rentingsData && (
            <div className="flex justify-start md:justify-end">
              <Pagination
                totalItems={rentingsData.total}
                itemsPerPage={RENTINGS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>

        <TableColumnsFilter
          columns={CLIENT_RENTINGS_COLUMNS}
          storageKey={CLIENT_RENTINGS_TABLE_STORAGE_KEY}
          defaultColumns={DEFAULT_VISIBLE_COLUMNS}
        >
          <div className="flex items-center gap-4 mb-4">
            <ColumnFilter
              size="sm"
              buttonText={t('filters.columns')}
              className="w-auto"
            />
          </div>

          {rentingsLoading || carsLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <Loader />
            </div>
          ) : (
            <>
              <RentingsTable
                rentings={rentingsData?.data || []}
                cars={cars || []}
                isLoading={false}
                hideClientColumn={true}
              />
              <div className="flex justify-start md:justify-end mt-4">
                <Pagination
                  totalItems={rentingsData?.total || 0}
                  itemsPerPage={RENTINGS_PER_PAGE}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </TableColumnsFilter>
      </div>

      {/* Verification Modal */}
      <ClientVerificationModal
        isOpen={isVerificationOpen}
        onClose={closeVerificationModal}
        clientId={id!}
      />
    </>
  );
};

export default ClientDetailsPage;
